import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

import {
  checkGPS,
  checkLocationPermission,
  getLocation,
  getReverseGeoCodeData,
  lastLocationFetch,
  requestGPS,
  requestLocationPermission,
  saveLastLoc,
} from "core-lib/utils/locationV2";
import { PLATFORM } from "core-lib/data/common";
import { getPlatform } from "core-lib/utils/helpers";
import {
  updateLocationRequest,
  updateLocationResponse,
} from "core-lib/reduxStore/layout/action";
import { AddressStatusType } from "core-lib/types/Address";
import { NativePermission } from "core-lib/types/NativePermission";
import {
  LocationRequestResponse,
  UserGeoAddressType,
  UserLocation,
  UserLocationData,
  UserLocationRequestType,
  UserLocationRequirementType,
} from "core-lib/types/Location";

export type AskLocationState =
  | "GPS"
  | "LOCATION"
  | "REQUEST_GPS"
  | "REQUEST_LOCATION"
  | "NONE";

type AskLocationContext = {
  type: "GPS" | "LOCATION";
  requestFromUser: boolean;
  locationPermission?: NativePermission;
};

export const useLocationHandler = () => {
  const dispatch = useDispatch();

  const locationRequest: UserLocation = useSelector(
    (state: any) => state.layout?.userLocationRequest
  );

  const userLocData: UserLocationData = useSelector(
    (state: any) => state.layout?.userLocationResponse?.data
  );

  const [askLocation, setAskLocation] = useState<AskLocationState>("NONE");

  const updateAndSave = (
    userLoc: UserLocationData,
    updates: Partial<UserLocationData>
  ) => {
    const updatedLoc = { ...userLoc, ...updates };
    dispatch(updateLocationResponse({ data: { ...updatedLoc } }));
    dispatch(
      updateLocationRequest({
        ...locationRequest,
        payload:
          updatedLoc.status === AddressStatusType.Success
            ? null
            : locationRequest.payload,
        requestType: UserLocationRequestType.Default,
      })
    );
  };

  const shouldShowBottomSheetOnceIn12Hours = (): boolean => {
    const lastShown = localStorage.getItem("locationPermissionBottomSheet");
    const now = Date.now();
    const twelveHours = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
    return !lastShown || now - parseInt(lastShown) >= twelveHours;
  };
  const tryLastLocationFallback = async (
    userLoc: UserLocationData
  ): Promise<boolean> => {
    if (!locationRequest?.payload?.useCurrentLocOnly) {
      const lastLocation = await lastLocationFetch();
      let obj: any = null;
      if (lastLocation) {
        if (
          locationRequest.requestType === UserLocationRequestType.RequestLatLng
        ) {
          obj = {
            coords: lastLocation,
          };
        } else {
          const { response } = await getReverseGeoCodeData({
            locationType: UserGeoAddressType.LatLng,
            latLng: lastLocation,
          });
          if (response) {
            obj = {
              coords: lastLocation,
              data: response,
            };
          } else {
            obj = null;
          }
        }
      }
      if (obj) {
        updateAndSave(userLoc, {
          ...obj,
          isLastKnownLocation: true,
          status: AddressStatusType.Success,
        });
        return true;
      }
    }
    return false;
  };

  const openBottomSheetIfRequired = async ({
    type,
    requestFromUser,
    locationPermission,
  }: AskLocationContext): Promise<boolean> => {
    if (
      locationRequest.payload.locationRequirementType ===
      UserLocationRequirementType.NotRequired
    ) {
      return false;
    }

    const { isIntimatedOnUserAction, payload } = locationRequest;

    const shouldShow =
      isIntimatedOnUserAction ||
      requestFromUser ||
      payload.locationRequirementType === UserLocationRequirementType.Mandatory;

    if (!shouldShow && !shouldShowBottomSheetOnceIn12Hours()) return false;

    const askMap: { [key: string]: AskLocationState } = {
      GPS: requestFromUser ? "GPS" : "REQUEST_GPS",
      LOCATION:
        requestFromUser || locationPermission !== NativePermission.Denied
          ? "LOCATION"
          : "REQUEST_LOCATION",
    };

    setAskLocation(askMap[type]);

    // Update the timestamp when the bottom sheet is shown
    localStorage.setItem(
      "locationPermissionBottomSheet",
      Date.now().toString()
    );
    return true;
  };

  const handleGpsUnavailable = async (
    userLoc: UserLocationData,
    requestGpsFromUser: boolean
  ): Promise<boolean> => {
    const fallback = await tryLastLocationFallback(userLoc);
    if (fallback) return true;
    if (getPlatform() === PLATFORM.android && requestGpsFromUser) {
      const isGpsEnabled = await requestGPS();
      if (isGpsEnabled) return false;
    }
    const bsShown = await openBottomSheetIfRequired({
      type: "GPS",
      requestFromUser: requestGpsFromUser,
    });
    if (!bsShown) {
      updateAndSave(userLoc, { status: AddressStatusType.Failed });
    }
    return true;
  };

  const handleLocationPermission = async (
    permission: NativePermission,
    userLoc: UserLocationData,
    requestLocationFromUser: boolean
  ): Promise<boolean> => {
    let locationPermission = permission;
    if (
      requestLocationFromUser &&
      locationPermission === NativePermission.Denied
    ) {
      const requestedPerm = await requestLocationPermission();
      if (requestedPerm) locationPermission = requestedPerm;
    }
    if (locationPermission === NativePermission.Accepted) {
      return false;
    }
    const fallback = await tryLastLocationFallback(userLoc);
    if (fallback) return true;
    const bsShown = await openBottomSheetIfRequired({
      type: "LOCATION",
      requestFromUser: requestLocationFromUser,
      locationPermission,
    });
    if (!bsShown) {
      updateAndSave(userLoc, { status: AddressStatusType.Failed });
    }
    return true;
  };

  const locationLatLng = async (): Promise<LocationRequestResponse | null> => {
    const givenLatLng = locationRequest.payload.givenLatLng;
    if (givenLatLng) return givenLatLng;

    if (
      locationRequest.requestType === UserLocationRequestType.RequestGeoAddress
    ) {
      return null;
    }

    const coordinates = await getLocation(locationRequest.payload.locationType);

    if (coordinates) {
      return coordinates;
    }

    toast.error("Unable to fetch your location");
    return null;
  };

  const locationMaster = async (requestUser = false) => {
    let userLoc: UserLocationData = {
      status: AddressStatusType.Pending,
      coords: null,
      data: null,
    };
    dispatch(updateLocationResponse({ data: { ...userLoc } }));
    const gpsPermission = await checkGPS();
    if (!gpsPermission) {
      const handled = await handleGpsUnavailable(userLoc, requestUser);
      if (handled) return;
    }

    const locationPermission = await checkLocationPermission();
    if (locationPermission) {
      const handled = await handleLocationPermission(
        locationPermission,
        userLoc,
        requestUser
      );
      if (handled) return;
    }
    const latLng = await locationLatLng();
    if (!latLng) {
      updateAndSave(userLoc, { status: AddressStatusType.Failed });
      return;
    }

    saveLastLoc(latLng);

    userLoc = {
      ...userLoc,
      coords: latLng,
      data: {
        latitude: latLng.lat,
        longitude: latLng.lon,
        locality: "",
      },
    };

    if (locationRequest.requestType === UserLocationRequestType.RequestLatLng) {
      updateAndSave(userLoc, { status: AddressStatusType.Success });
      return;
    }

    const { response: geoAddress } = await getReverseGeoCodeData({
      locationType: UserGeoAddressType.LatLng,
      latLng,
    });

    updateAndSave(userLoc, {
      data: geoAddress || userLoc.data,
      status: geoAddress ? AddressStatusType.Success : AddressStatusType.Failed,
    });
  };

  const onManualCloseBtmSheet = () => {
    dispatch(
      updateLocationResponse({
        data: { ...userLocData, status: AddressStatusType.Failed },
      })
    );
    setAskLocation("NONE");
  };

  useEffect(() => {
    if (
      locationRequest &&
      locationRequest.requestType !== UserLocationRequestType.Default
    ) {
      locationMaster();
    }
  }, [locationRequest]);

  return {
    askLocation,
    setAskLocation,
    locationRequest,
    locationMaster,
    onManualCloseBtmSheet,
  };
};
